import { useState } from "react";

import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleQuestion,
  faBell,
  faChevronDown,
  faGear,
  faRightFromBracket,
  faBars
} from "@fortawesome/free-solid-svg-icons";

import Link from "../../atoms/mui/Link";
import { useSupportSlider } from "../../atoms/buttons/TopBarSupportIcon";

import {
  useAccountData,
  useAuth,
  useCollectionDataListener
} from "../../../helpers/firebase";
import { useLogout } from "../../../helpers/components/accountAccess";
import { useProductsTabContext } from "../../../helpers/components/explorerProductsTab";
import { useCustomersTabContext } from "../../../helpers/components/explorerCustomersTab";
import { useSuppliersTabContext } from "../../../helpers/components/explorerSuppliersTab";

const buildInitials = (name) =>
  (name || "")
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const buildBomRequestItems = (pendingBomRequests) =>
  (pendingBomRequests || []).map(({ lifecycleEventId, productId, productName, buyerName }) => ({
    key: `bom-${lifecycleEventId}`,
    label: `${productName} requested by ${buyerName || "a customer"}`,
    href: `/explorer/dashboard?view=products&selectedId=${productId}`
  }));

const buildSupplierShareItems = (pendingSupplierShares) =>
  (pendingSupplierShares || []).map(({ relationshipId, vendorName, year }) => ({
    key: `supplier-${relationshipId}-${year}`,
    label: `${vendorName} — ${year} survey share pending`,
    href: `/explorer/dashboard?view=suppliers&selectedId=${relationshipId}`
  }));

const buildCustomerRequestItems = (pendingRequests) =>
  (pendingRequests || []).map(({ relationshipId, buyerId, buyerName, year }) => ({
    key: `customer-${relationshipId}-${year}`,
    label: `${buyerName || "A customer"} requested ${year} data`,
    href: `/explorer/dashboard?view=customers&selectedId=${buyerId}`
  }));

const ExplorerTopBar = ({ title, onMenuClick }) => {
  const { palette } = useTheme();
  const logout = useLogout();
  const { openSupport, supportSliderJsx } = useSupportSlider();

  const [accountData] = useAccountData();
  const [user] = useAuth();
  const [customers] = useCollectionDataListener("customers");
  const { pendingBomRequests } = useProductsTabContext();
  const { pendingSupplierShares } = useSuppliersTabContext();
  const { pendingRequests } = useCustomersTabContext(customers || []);

  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  const companyName = accountData?.name || "Your company";
  const userName = user?.displayName || "";
  const initials = buildInitials(userName || companyName);

  const notificationItems = [
    ...buildBomRequestItems(pendingBomRequests),
    ...buildSupplierShareItems(pendingSupplierShares),
    ...buildCustomerRequestItems(pendingRequests)
  ];

  const pendingCount = notificationItems.length;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: palette.common.white,
        borderBottom: `1px solid ${palette.backgroundGray.main}`,
        px: { xs: 2, md: 3 },
        py: 1.5,
        minHeight: 56
      }}
    >
      {supportSliderJsx}
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={onMenuClick}
          size="small"
          sx={{ display: { xs: "inline-flex", md: "none" } }}
        >
          <FontAwesomeIcon
            icon={faBars}
            style={{ fontSize: 18, color: palette.text.secondary }}
          />
        </IconButton>
        <Typography variant="h6" color="textPrimary">
          {title}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Help">
          <IconButton onClick={openSupport} size="small">
            <FontAwesomeIcon
              icon={faCircleQuestion}
              style={{ fontSize: 18, color: palette.text.secondary }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Pending activity">
          <IconButton
            onClick={(event) => setNotifAnchorEl(event.currentTarget)}
            size="small"
            aria-label="Pending activity"
          >
            <Badge badgeContent={pendingCount} color="error">
              <FontAwesomeIcon
                icon={faBell}
                style={{ fontSize: 18, color: palette.text.secondary }}
              />
            </Badge>
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={() => setNotifAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {notificationItems.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">
                No pending activity
              </Typography>
            </MenuItem>
          )}
          {notificationItems.length > 0 && (
            <ListSubheader>Pending activity</ListSubheader>
          )}
          {notificationItems.map(({ key, label, href }) => (
            <Link key={key} href={href} style={{ textDecoration: "none", color: "inherit" }}>
              <MenuItem onClick={() => setNotifAnchorEl(null)}>
                <Typography variant="body2" color="textPrimary">
                  {label}
                </Typography>
              </MenuItem>
            </Link>
          ))}
        </Menu>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          onClick={(event) => setAccountAnchorEl(event.currentTarget)}
          sx={{ cursor: "pointer", pl: 1 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: palette.secondary.main,
              color: palette.common.white,
              fontSize: 13
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Typography variant="subtitle2" color="textPrimary">
              {companyName}
            </Typography>
            {userName && (
              <Typography variant="caption" color="textSecondary">
                {userName}
              </Typography>
            )}
          </Box>
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{ fontSize: 12, color: palette.text.secondary }}
          />
        </Box>
        <Menu
          anchorEl={accountAnchorEl}
          open={Boolean(accountAnchorEl)}
          onClose={() => setAccountAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Link
            href="/explorer/dashboard/settings"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <MenuItem onClick={() => setAccountAnchorEl(null)}>
              <FontAwesomeIcon
                icon={faGear}
                style={{ fontSize: 14, marginRight: 10 }}
              />
              <Typography variant="body2" color="textPrimary">
                Settings
              </Typography>
            </MenuItem>
          </Link>
          <Divider />
          <MenuItem onClick={() => logout()}>
            <FontAwesomeIcon
              icon={faRightFromBracket}
              style={{ fontSize: 14, marginRight: 10 }}
            />
            <Typography variant="body2" color="textPrimary">
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default ExplorerTopBar;
